<template>
    <div>
        <div v-if="editable">
            <div class="flex">
                <div>
                    <b>{{ user?.name }}</b>
                </div>
                <div class="mx-2 mb-2 text-[14px] text-muted">
                    {{ $filters.fullDate(today, currentLocale) }}
                </div>
            </div>
            <jet-textarea
                v-model="form.comment"
                :horizontal="false"
                :inline="true"
                :invalid-feedback="form.errors.comment"
                :placeholder="placeholder"
                :resizable="true"
                :rows="2"
                class="mt-1"
                @input="emit('input', form.comment)"
                @keydown="handleTextareaKeydown"
            />
            <div>
                <jet-button
                    :disabled="!form.isDirty"
                    :is-processing="form.processing"
                    class="btn-text mx-2 min-w-0 p-0 !text-[14px]"
                    variant="tertiary"
                    @click.stop="submitForm"
                    >{{ trans('comments.action.post') }}
                </jet-button>
            </div>
            <alert v-if="showWarning" ref="warning" class="mt-3" color="warning">{{ trans('comments.warning.unsaved_changes') }} </alert>
        </div>
        <div v-if="comments" :key="componentKey" class="comments-list mx-2 mt-3">
            <div v-for="comment in comments" :key="comment.id" class="mb-3">
                <div class="flex items-center">
                    <div>
                        <b>{{ comment.author?.name }}</b>
                    </div>
                    <div class="mx-2 text-sm text-muted">
                        {{ $filters.relativeDate(comment.createdAt, currentLocale) }}
                    </div>
                    <span v-if="comment.origin === 'EXTERNAL'" class="text-sm text-muted"> · {{ trans('comments.origin.external') }} </span>
                    <span v-else-if="comment.syncStatus === 'FAILED'" class="text-sm text-danger"> · {{ trans('comments.sync.failed') }} </span>
                </div>
                <div class="comment" v-html="$filters.linkify(comment.comment)" />
            </div>
        </div>
    </div>
</template>
<script setup>
import JetTextarea from '@/Jetstream/Textarea.vue';
import { DateTime } from 'luxon';
import JetButton from '@/Jetstream/Button.vue';
import { eventBus, events } from '@/eventBus';
import Alert from '@/Components/Alert.vue';
import { $http } from '@/bootstrap.js';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useForm, usePage } from '@inertiajs/vue3';

defineOptions({ name: 'comments' });

const props = defineProps({
    initialComments: {
        type: Array,
        default: () => [],
    },
    showWarning: { type: Boolean, default: false },
    placeholder: { type: String },
    editable: { type: Boolean, default: true },
    createCommentUrl: { type: String, required: true },
    getCommentsUrl: { type: String, required: true },
    // When > 0, re-fetch comments on this cadence (ms) while one of our own comments is still
    // syncing, so its PENDING → SYNCED/FAILED status updates without a full page reload. Comments
    // authored on the CMMS side arrive via the backend sync and appear on the next page load.
    pollInterval: { type: Number, default: 0 },
});

const emit = defineEmits(['input']);

const comments = ref([]);
const componentKey = ref(0);
const form = useForm({ comment: '' });
const warning = ref(null);
let pollTimer = null;
let pollInFlight = false;

const page = usePage();
const user = computed(() => page.props?.auth?.user ?? page.props?.user ?? null);
const today = computed(() => DateTime.fromJSDate(new Date()));

watch(
    () => props.showWarning,
    (value) => {
        if (value) {
            nextTick(() => {
                warning.value?.$el?.scrollIntoView({ behavior: 'smooth' });
            });
        }
    },
);

onMounted(() => {
    comments.value = props.initialComments;
    eventBus.$on(events.clockUpdated, () => {
        forceRerender();
    });

    startPolling();
});

onBeforeUnmount(() => {
    eventBus.$off(events.clockUpdated);
    stopPolling();
});

// A comment is only "in flight" while its push to the CMMS is unresolved. SYNCED/FAILED/external
// comments are settled and won't change again until the next backend sync (which lands on a page
// reload), so there's nothing to poll for once nothing is pending.
function hasPendingSync() {
    return comments.value.some((comment) => comment?.syncStatus === 'PENDING' || comment?.syncStatus === 'PUSHING');
}

// Poll only while a comment is still syncing, and only when enabled (pollInterval > 0).
function startPolling() {
    if (props.pollInterval > 0 && pollTimer === null && hasPendingSync()) {
        pollTimer = setInterval(poll, props.pollInterval);
    }
}

function stopPolling() {
    if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
}

// Skip if a poll is still in flight so slow responses don't stack up.
function poll() {
    if (!pollInFlight) {
        fetchComments();
    }
}

function fetchComments() {
    pollInFlight = true;
    $http
        .get(props.getCommentsUrl)
        .then((resp) => {
            comments.value = resp.data;
        })
        .catch(() => {
            // Transient fetch failure; the next poll retries.
        })
        .finally(() => {
            pollInFlight = false;
            // Stop once everything has settled (all SYNCED/FAILED); a freshly posted comment
            // restarts polling from submitForm.
            if (!hasPendingSync()) {
                stopPolling();
            }
        });
}

function forceRerender() {
    componentKey.value += 1;
}

function handleTextareaKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        const value = (form.comment ?? '').trim();
        if (value.length > 0 && !form.processing) {
            event.preventDefault();
            submitForm();
        }
    }
}

function submitForm() {
    const value = (form.comment ?? '').trim();
    if (value.length === 0 || form.processing) {
        return;
    }

    form.processing = true;

    $http
        .post(props.createCommentUrl, form.data())
        .then((resp) => {
            comments.value = [resp.data, ...comments.value];
            emit('input', '');
            form.reset();
            form.clearErrors();
            // A CMMS-bound comment comes back PENDING — start polling to track it to SYNCED/FAILED.
            startPolling();
        })
        .catch((error) => {
            let errors = {};
            Object.keys(error.response?.data?.errors ?? {}).forEach((key) => {
                errors[key] = error.response.data.errors[key].join(' ');
            });
            form.setError(errors);
        })
        .finally(() => {
            form.processing = false;
        });
}
</script>
